import React from 'react';
import { usePoolData } from '../hooks/usePoolData.js';
import { MetricTooltip } from './MetricTooltip.jsx';

function SettingsPanelComponent() {
  const { settings, updateSettings, optimizationResults, runOptimization, isLoading } = usePoolData();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isOptimizing, setIsOptimizing] = React.useState(false);
  
  // Default settings if not available
  const defaultSettings = {
    analysisWindowHours: 24,
    hashrateWeight: 0.6,
    blockShareWeight: 0.4,
    confidenceLevel: 0.9,
    outlierPThreshold: 0.05,
    maxIterations: 3,
  };
  
  const [localSettings, setLocalSettings] = React.useState(settings || defaultSettings);

  React.useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  const handleChange = (key, value) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
  };

  const handleSave = () => {
    updateSettings(localSettings);
  };

  const handleReset = () => {
    const defaultSettings = {
      analysisWindowHours: 24,
      hashrateWeight: 0.6,
      blockShareWeight: 0.4,
      confidenceLevel: 0.9,
      outlierPThreshold: 0.05,
      maxIterations: 3,
    };
    setLocalSettings(defaultSettings);
    updateSettings(defaultSettings);
  };

  const handleAutoOptimize = async () => {
    setIsOptimizing(true);
    try {
      const result = await runOptimization();
      if (result) {
        // Settings are auto-applied by runOptimization
        // Update local state to reflect new values
        setLocalSettings({
          ...localSettings,
          outlierPThreshold: result.bestParams.outlierPThreshold,
          hashrateWeight: result.bestParams.hashrateWeight,
          blockShareWeight: 1 - result.bestParams.hashrateWeight,
        });
      }
    } catch (err) {
      console.error('Optimization failed:', err);
    } finally {
      setIsOptimizing(false);
    }
  };

  return (
    <div className="bg-navy-light border border-dark rounded mt-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-navy transition-colors"
      >
        <h2 className="text-primary text-xl font-bold">Settings</h2>
        <span className="text-primary text-sm">
          {isExpanded ? '▼' : '▶'}
        </span>
      </button>

      {isExpanded && (
        <div className="p-4 pt-0 space-y-4 border-t border-dark">
          {/* Analysis Window Hours */}
          <div className="bg-navy border border-dark p-4 rounded">
            <label className="block text-primary text-sm mb-2">
              Analysis Window (hours) <MetricTooltip metricKey="analysisWindow" position="right" />
            </label>
            <p className="text-primary text-xs mb-2 opacity-75">
              Time window for historical block analysis (default: 24 hours)
            </p>
            <input
              type="number"
              min="1"
              max="168"
              step="1"
              value={localSettings.analysisWindowHours}
              onChange={(e) => handleChange('analysisWindowHours', parseFloat(e.target.value) || 24)}
              className="w-full px-3 py-2 bg-navy-light border border-dark rounded text-primary focus:outline-none focus:ring-2 focus:ring-accent-orange"
            />
            <p className="text-primary text-xs mt-1">
              Current: {localSettings.analysisWindowHours} hours
            </p>
          </div>

          {/* Stable Share Weights */}
          <div className="bg-navy border border-dark p-4 rounded">
            <label className="block text-primary text-sm mb-2">
              Stable Share Calculation Weights <MetricTooltip metricKey="stableShareWeights" position="right" />
            </label>
            <p className="text-primary text-xs mb-2 opacity-75">
              Weights for calculating stable share: hashrateShare × weight1 + blockShare × weight2
            </p>
            <div className="space-y-2">
              <div>
                <label className="block text-primary text-xs mb-1">
                  Hashrate Weight: {localSettings.hashrateWeight.toFixed(2)} <MetricTooltip metricKey="hashrateWeight" position="right" />
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={localSettings.hashrateWeight}
                  onChange={(e) => {
                    const newHashrateWeight = parseFloat(e.target.value);
                    const newBlockShareWeight = 1 - newHashrateWeight;
                    setLocalSettings({
                      ...localSettings,
                      hashrateWeight: newHashrateWeight,
                      blockShareWeight: newBlockShareWeight,
                    });
                  }}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-primary text-xs mb-1">
                  Block Share Weight: {localSettings.blockShareWeight.toFixed(2)} <MetricTooltip metricKey="blockShareWeight" position="right" />
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={localSettings.blockShareWeight}
                  onChange={(e) => {
                    const newBlockShareWeight = parseFloat(e.target.value);
                    const newHashrateWeight = 1 - newBlockShareWeight;
                    setLocalSettings({
                      ...localSettings,
                      hashrateWeight: newHashrateWeight,
                      blockShareWeight: newBlockShareWeight,
                    });
                  }}
                  className="w-full"
                />
              </div>
            </div>
            <p className="text-primary text-xs mt-2">
              Sum: {(localSettings.hashrateWeight + localSettings.blockShareWeight).toFixed(2)}
            </p>
          </div>

          {/* Confidence Level */}
          <div className="bg-navy border border-dark p-4 rounded">
            <label className="block text-primary text-sm mb-2">
              Confidence Level <MetricTooltip metricKey="confidenceLevel" position="right" />
            </label>
            <p className="text-primary text-xs mb-2 opacity-75">
              Confidence level for deviation bands and intervals (default: 0.9 = 90%)
            </p>
            <input
              type="number"
              min="0.5"
              max="0.99"
              step="0.01"
              value={localSettings.confidenceLevel}
              onChange={(e) => handleChange('confidenceLevel', parseFloat(e.target.value) || 0.9)}
              className="w-full px-3 py-2 bg-navy-light border border-dark rounded text-primary focus:outline-none focus:ring-2 focus:ring-accent-orange"
            />
            <p className="text-primary text-xs mt-1">
              Current: {(localSettings.confidenceLevel * 100).toFixed(0)}%
            </p>
          </div>

          {/* Outlier Detection Threshold */}
          <div className="bg-navy border border-dark p-4 rounded">
            <label className="block text-primary text-sm mb-2">
              Outlier Detection Threshold (p-value) <MetricTooltip metricKey="outlierThreshold" position="right" />
            </label>
            <p className="text-primary text-xs mb-2 opacity-75">
              Probability threshold for marking block intervals as outliers (default: 0.05 = 5% tails)
            </p>
            <input
              type="number"
              min="0.01"
              max="0.15"
              step="0.01"
              value={localSettings.outlierPThreshold || 0.05}
              onChange={(e) => handleChange('outlierPThreshold', parseFloat(e.target.value) || 0.05)}
              className="w-full px-3 py-2 bg-navy-light border border-dark rounded text-primary focus:outline-none focus:ring-2 focus:ring-accent-orange"
            />
            <p className="text-primary text-xs mt-1">
              Current: {((localSettings.outlierPThreshold || 0.05) * 100).toFixed(1)}%
            </p>
          </div>

          {/* Max Outlier Detection Iterations */}
          <div className="bg-navy border border-dark p-4 rounded">
            <label className="block text-primary text-sm mb-2">
              Max Outlier Detection Iterations <MetricTooltip metricKey="maxIterations" position="right" />
            </label>
            <p className="text-primary text-xs mb-2 opacity-75">
              Maximum iterations for iterative outlier refinement (default: 3)
            </p>
            <input
              type="number"
              min="1"
              max="10"
              step="1"
              value={localSettings.maxIterations || 3}
              onChange={(e) => handleChange('maxIterations', parseInt(e.target.value) || 3)}
              className="w-full px-3 py-2 bg-navy-light border border-dark rounded text-primary focus:outline-none focus:ring-2 focus:ring-accent-orange"
            />
            <p className="text-primary text-xs mt-1">
              Current: {localSettings.maxIterations || 3} iterations
            </p>
          </div>

          {/* Parameter Optimization Section */}
          <div className="bg-navy border border-dark p-4 rounded">
            <h3 className="text-primary text-sm font-semibold mb-2">Parameter Optimization <MetricTooltip metricKey="parameterOptimization" position="right" /></h3>
            <p className="text-primary text-xs mb-3 opacity-75">
              Automatically find the best outlier threshold and hashrate weight by minimizing prediction error (MAE) on historical data.
            </p>
            
            <button
              onClick={handleAutoOptimize}
              disabled={isOptimizing || isLoading}
              className="w-full px-4 py-2 bg-accent-orange text-navy font-semibold rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed mb-3"
            >
              {isOptimizing ? 'Optimizing... (may take 10s)' : '🔍 Auto-Optimize Parameters'}
            </button>
            
            {optimizationResults && (
              <div className="bg-navy-light border border-dark p-3 rounded space-y-2">
                <p className="text-accent-orange text-xs font-semibold">Optimization Results:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-primary opacity-75">Best Outlier Threshold:</span>
                    <span className="text-primary font-semibold ml-1">
                      {(optimizationResults.bestParams.outlierPThreshold * 100).toFixed(1)}%
                    </span>
                    <MetricTooltip metricKey="outlierThreshold" position="right" />
                  </div>
                  <div>
                    <span className="text-primary opacity-75">Best Hashrate Weight:</span>
                    <span className="text-primary font-semibold ml-1">
                      {optimizationResults.bestParams.hashrateWeight.toFixed(2)}
                    </span>
                    <MetricTooltip metricKey="hashrateWeight" position="right" />
                  </div>
                  <div>
                    <span className="text-primary opacity-75">Best MAE:</span>
                    <span className="text-primary font-semibold ml-1">
                      {optimizationResults.bestMAE.toFixed(2)}s
                    </span>
                    <MetricTooltip metricKey="mae" position="right" />
                  </div>
                  <div>
                    <span className="text-primary opacity-75">Combinations Tested:</span>
                    <span className="text-primary font-semibold ml-1">
                      {optimizationResults.totalCombinations}
                    </span>
                  </div>
                </div>
                <p className="text-primary text-xs opacity-75 mt-2">
                  Parameters have been automatically applied. Click "Save Settings" to persist.
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 bg-accent-orange text-navy font-semibold rounded hover:opacity-90 transition-opacity"
            >
              {optimizationResults ? 'Save Optimized Settings' : 'Save Settings'}
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-navy border border-dark text-primary rounded hover:bg-navy-light transition-colors"
            >
              Reset to Defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const SettingsPanel = React.memo(SettingsPanelComponent);

